import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, User, Loader2, X } from "lucide-react";

const EMPTY = { email: "", password: "", name: "", role: "member" };

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setMembers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || form.password.length < 6)
      return toast.error("Email valide + mot de passe (6 caractères min.) requis");
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Compte créé");
      setForm(EMPTY);
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Supprimer le compte ${email} ?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Compte supprimé");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const field =
    "w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors";

  return (
    <>
      <PageHeader overline="Accès sécurisés · Collaborateurs" title="Équipe">
        <button
          data-testid="add-member-button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 hover:-translate-y-[1px] transition-all duration-150"
        >
          <UserPlus className="h-4 w-4" />
          Créer un accès
        </button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
          Seul l'administrateur peut créer des accès. Les collaborateurs se connectent avec
          l'email et le mot de passe que tu définis ici — pas d'inscription publique.
        </p>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="border border-white/10 rounded-sm bg-secondary" data-testid="team-list">
            {members.map((m) => (
              <div
                key={m.id}
                data-testid={`member-${m.id}`}
                className="flex items-center gap-4 p-4 border-b border-white/10 last:border-b-0"
              >
                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                  {m.role === "admin" ? (
                    <Shield className="h-4 w-4 text-accent" />
                  ) : (
                    <User className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                  <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider border rounded-sm px-2 py-1 ${
                    m.role === "admin"
                      ? "text-accent border-accent/40"
                      : "text-muted-foreground border-white/20"
                  }`}
                >
                  {m.role === "admin" ? "Admin" : "Membre"}
                </span>
                {m.id !== user?.id && (
                  <button
                    data-testid={`delete-member-${m.id}`}
                    onClick={() => remove(m.id, m.email)}
                    className="p-2 text-muted-foreground hover:text-accent transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <form
            onSubmit={submit}
            data-testid="member-form"
            className="bg-[#111111] border border-white/15 rounded-sm w-full max-w-md p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl font-bold tracking-tight">Nouvel accès</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Nom
                </label>
                <input
                  data-testid="member-name-input"
                  className={field}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Prénom Nom"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Email *
                </label>
                <input
                  data-testid="member-email-input"
                  type="email"
                  className={field}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="collaborateur@email.com"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Mot de passe *
                </label>
                <input
                  data-testid="member-password-input"
                  type="text"
                  className={field}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="6 caractères minimum"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">
                  Rôle
                </label>
                <div className="flex gap-2">
                  {["member", "admin"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      data-testid={`role-${r}`}
                      onClick={() => setForm({ ...form, role: r })}
                      className={`flex-1 py-2.5 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${
                        form.role === r
                          ? "bg-primary border-primary text-primary-foreground"
                          : "border-white/15 text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {r === "admin" ? "Admin" : "Membre"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-1 border border-white/15 rounded-sm py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                data-testid="save-member-button"
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground rounded-sm py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Créer l'accès
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
